import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useCanonical";
import { getStaticRouteSeo } from "@/lib/seo";
import { buildWorkJsonLd } from "@/lib/structuredData";

interface Job {
  id: string;
  title: string;
  slug: string;
  categories: {
    slug: string;
  };
  images: {
    url: string;
  }[];
}

const WorkAll = () => {
  const [shuffledJobs, setShuffledJobs] = useState<Job[]>([]);

  const { data: jobs = [] } = useQuery({
    queryKey: ["all-jobs-with-images"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select(`
          id,
          title,
          slug,
          categories!inner(slug)
        `)
        .neq("categories.slug", "hej-mamma")
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      const allJobs = data || [];

      // Use the same cover image as the category pages (first image of each job)
      const { data: firstImages, error: imgError } = await supabase.rpc(
        "get_first_images_for_jobs",
        { job_ids: allJobs.map((job) => job.id) },
      );

      if (imgError) throw imgError;

      const urlByJob = new Map((firstImages || []).map((img) => [img.job_id, img.url]));

      // Keep only jobs that have at least one image
      return allJobs
        .filter((job) => urlByJob.has(job.id))
        .map((job) => ({ ...job, images: [{ url: urlByJob.get(job.id) as string }] })) as Job[];
    },
  });

  const jsonLd = useMemo(() => {
    return buildWorkJsonLd({
      items: jobs.map((job) => ({
        url: `https://www.inanederdal.se/work/${job.categories.slug}/${job.slug}`,
        name: job.title,
        image: job.images[0]?.url,
      })),
    });
  }, [jobs]);

  const seo = getStaticRouteSeo("work", { image: jobs[0]?.images[0]?.url });

  useSEO({ ...seo, jsonLd });

  useEffect(() => {
    if (jobs.length > 0) {
      // Shuffle the jobs array
      const shuffled = [...jobs].sort(() => Math.random() - 0.5);
      setShuffledJobs(shuffled);
    }
  }, [jobs]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="pt-32 md:pt-40 pb-24 flex-1">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {shuffledJobs.map((job) => (
              <Link
                key={job.id}
                to={`/work/${job.categories.slug}/${job.slug}`}
                className="group block"
              >
                <div className="aspect-[4/5] overflow-hidden bg-muted">
                  <img
                    src={job.images[0]?.url}
                    alt={job.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <p className="mt-3 text-sm text-foreground/80 group-hover:text-accent transition-colors border-b border-transparent group-hover:border-accent inline-block">
                  {job.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WorkAll;
